/*
    Whether a QR code link will actually resolve.

    Rachel, 22 Oct 2026: "the QR code took me to the Motimatic website but a 'page not found'".
    Every layer behaved -- the token was substituted per piece, Lob printed a scannable code, the
    scan was counted -- and the URL simply never existed, because `{{code}}` is a different value
    on every card and no site has a route for it.

    Nobody chose it on her card. It was on three approved prod mail pieces while the postcard
    templates they descend from all carry a blank destination, so it was authored once on the
    earliest card and travelled to the other two by copy. To whoever opened one it read as a
    default, which is why the old hint under this field -- "add {{code}} so a scan says who" --
    had nothing to push back against.

    The mistake is invisible everywhere it could be caught. Invisible on the card, which scans
    perfectly and then dead-ends. Invisible in reporting, because Lob counts the scan before
    redirecting, so `scan_count` looks healthy. Visible only to the person holding the postcard.

    So it is said here, in the editor, while there is still something to fix. The same rule is
    enforced server-side in `DirectMailContentIn._check_qr_destination`, because this form is not
    the only way in.
*/

export interface QrDestinationProblem {
    /** `error` cannot resolve and the server refuses it. `warning` resolves, but somewhere odd. */
    severity: "error" | "warning";
    message: string;
}

/*
    Deliberately a string match rather than `new URL()`.

    `new URL("https://x.edu/{{code}}").pathname` is `/%7B%7Bcode%7D%7D` -- the braces are
    percent-encoded, so looking for `{{code}}` in the parsed path never finds it and the one
    shape this exists to catch sails through. The raw string is what somebody typed and what
    gets printed, so the raw string is what gets checked.
*/
const HTTP_URL = /^https?:\/\/([^/?#]+)([^?#]*)/i;

/** Our own marketing site: somewhere a prospective student should never be sent. */
const OURS = /(^|\.)motimatic\.com$/i;

export function qrDestinationProblem(destination: string): QrDestinationProblem | null {
    const trimmed = destination.trim();

    // Blank means no QR code, which is a legitimate card rather than a half-finished one.
    if (!trimmed) return null;

    const parts = HTTP_URL.exec(trimmed);
    if (!parts) {
        return {
            severity: "error",
            message: "A QR code link needs the whole address, starting with https://",
        };
    }

    const [, host, path] = parts;

    if (path.includes("{{code}}")) {
        return {
            severity: "error",
            message:
                "{{code}} becomes a different value on every card, so no page exists at that "
                + "address and the scan ends on a 404. Put it after a ? instead, like "
                + "?src={{code}}, or leave it out. Scans are counted either way.",
        };
    }

    if (OURS.test(host)) {
        return {
            severity: "warning",
            message:
                "This sends whoever scans it to the Motimatic site rather than the school's. "
                + "Fine for a test card, wrong for anything going to students.",
        };
    }

    return null;
}
